/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  Button,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  Box,
  CircularProgress,
  Paper,
  MenuList,
  MenuItem,
  ClickAwayListener,
  Typography
} from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { useFormik } from 'formik'
import { userGroupFormValidation } from '../../../../../utils/validationSchema'
import { FormLoader } from '../../../../Loader/FormLoader'
import { useStyles } from './Styles'
import { NamedRoutes } from '../../../../../routes'
import { useKeycloak } from '@react-keycloak/web'
import SubscriptionServices from '../../../../../Services/Consumers/SubscriptionServices'

const UserGroupForm = ({ userGroupData, onSubmit, handleNotificationCall }) => {
  const classes = useStyles()
  const intl = useIntl()
  const history = useHistory()
  const { keycloak } = useKeycloak()

  const [searchText, setSearchText] = React.useState('')
  const [isSearching, setIsSearching] = React.useState(false)
  const [searchResults, setSearchResults] = React.useState([])
  const [showDropdown, setShowDropdown] = React.useState(false)
  const [selectedUsers, setSelectedUsers] = React.useState(userGroupData?.users || [])
  const debounceTimer = React.useRef(null)

  const UserGroupForm = useFormik({
    initialValues: {
      name: userGroupData?.name || '',
      users: userGroupData?.users || []
    },
    validateOnMount: true,
    validateOnBlur: false,
    validateOnChange: true,
    validationSchema: userGroupFormValidation,
    onSubmit: ((values, actions) => {
      const UserGroupFormPayload = {
        name: values.name,
        user_ids: selectedUsers.map(user => user.id)
      }
      onSubmit(UserGroupFormPayload, actions)
    })
  })

  React.useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  const handleSearch = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setSearchResults([])
      setShowDropdown(false)
      return
    }
    setIsSearching(true)
    SubscriptionServices.searchUsers({
      headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` },
      params: { name: searchTerm }
    }).then((result) => {
      if (!!result && !!result?.data && result?.data?.status?.code === 200) {
        const filtered = result.data.data.filter((user) => !selectedUsers.find((sel) => sel.id === user.id))
        setSearchResults(filtered)
        setShowDropdown(true)
      } else {
        let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
        if (result.response && result.response.data) {
          if (result.response.data && result.response.data.status && result.response.data.status.errors) {
            message = result.response.data.status.errors
          }
        }
        handleNotificationCall({
          type: 'defence',
          message: message,
          color: 'error'
        }, 'error')
        setSearchResults([])
        setShowDropdown(false)
      }
    }).then(() => {
      setIsSearching(false)
    })
  }

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchText(value)
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }
    debounceTimer.current = setTimeout(() => {
      handleSearch(value)
    }, 500)
  }

  // Handle user selection
  const handleUserSelect = (user) => {
    const newSelectedUsers = [...selectedUsers, user]
    setSelectedUsers(newSelectedUsers)
    UserGroupForm.setFieldValue('users', newSelectedUsers)
    setSearchText('')
    setSearchResults([])
    setShowDropdown(false)
  }

  // Handle user removal
  const handleUserRemove = (userId) => {
    const newSelectedUsers = selectedUsers.filter(user => user.id !== userId)
    setSelectedUsers(newSelectedUsers)
    UserGroupForm.setFieldValue('users', newSelectedUsers)
  }

  return (
    <form onSubmit={UserGroupForm.handleSubmit} noValidate autoComplete="off"
      className={classes.formWrapper}>

      {/* Group Name Field */}
      <div className={classes.BasicInfoRow}>
        <InputLabel id="UserGroup.Name">
          <FormattedMessage id='Fields.label.UserGroupName' />
        </InputLabel>
        <FormControl variant="outlined">
          <TextField
            placeholder={intl.formatMessage({ id: 'UserGroup.placeholder.UserGroupName' })}
            id="UserGroup.Name"
            type={'text'}
            margin="dense"
            variant="outlined"
            name='name'
            value={UserGroupForm.values.name}
            onChange={UserGroupForm.handleChange}
            onBlur={UserGroupForm.handleBlur}
            helperText={UserGroupForm.errors.name && UserGroupForm.touched.name && UserGroupForm.errors.name}
            error={UserGroupForm.errors.name && UserGroupForm.touched.name !== undefined}
          />
        </FormControl>
      </div>

      {/* Users Autocomplete Field */}
      <div className={classes.BasicInfoRow}>
        <InputLabel id="UserGroup.Users">
          <FormattedMessage id='Fields.label.Users' />
        </InputLabel>
        <FormControl variant="outlined">
          <ClickAwayListener onClickAway={() => setShowDropdown(false)}>
            <div className={classes.autocompleteWrapper}>
              <TextField
                placeholder={intl.formatMessage({ id: 'UserGroup.label.SearchUsers' })}
                id="UserGroup.Users"
                type={'text'}
                margin="dense"
                variant="outlined"
                value={searchText}
                onChange={handleSearchChange}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowDropdown(true)
                  }
                }}
                onBlur={() => {
                  UserGroupForm.setFieldTouched('users', true)
                  if (!selectedUsers || selectedUsers.length === 0) {
                    UserGroupForm.setFieldError(
                      'users',
                      intl.formatMessage({ id: 'validationSchema.required' })
                    )
                  }
                }}
                InputProps={{
                  endAdornment: isSearching && <CircularProgress size={20} />
                }}
                helperText={UserGroupForm.errors.users && UserGroupForm.touched.users && UserGroupForm.errors.users}
                error={!!UserGroupForm.errors.users && !!UserGroupForm.touched.users}
              />

              {/* Dropdown Results */}
              {showDropdown && (
                <Paper className={classes.dropdown}>
                  {
                    searchResults.length > 0 ?
                      <MenuList>
                        {searchResults.map((user) => (
                          <MenuItem
                            key={user.id}
                            onClick={() => handleUserSelect(user)}
                            className={classes.menuItem}
                          >
                            <div>
                              <div className={classes.userName}>
                                {user.first_name} {user.last_name}
                              </div>
                              <div className={classes.userEmail}>
                                {user.email}
                              </div>
                            </div>
                          </MenuItem>
                        ))}
                      </MenuList>
                      :
                      <Typography variant="body1" className={classes.usersNoResultsText}>
                        {intl.formatMessage({ id: 'UserGroups.List.Users.NoResults' })}
                      </Typography>
                  }
                </Paper>
              )}

              {/* Selected Users */}
              {selectedUsers.length > 0 && (
                <Box className={classes.selectedUsersBox}>
                  {selectedUsers.map((user) => (
                    <Chip
                      key={user.id}
                      label={
                        (!!user.name ? user.name : `${user.first_name} ${user.last_name}`) +
                        (!!user.email && ` (${user.email})`)
                      }
                      onDelete={() => handleUserRemove(user.id)}
                      className={classes.userChip}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </div>
          </ClickAwayListener>
        </FormControl>
      </div>

      {/* Submit Buttons */}
      <div className={classes.SubmitRow}>
        <button
          type="submit"
          disabled={UserGroupForm.isSubmitting || !UserGroupForm.isValid}
          className={classes.AddAdminSubmit}
        >
          <FormattedMessage id='AdditionalInfo.button.submit' />
        </button>
        <Button onClick={() => history.push(NamedRoutes.subscription.userGroups.list)} color="primary">
          <FormattedMessage id='ChangePhone.DialogActions.cancel' />
        </Button>
      </div>
      {UserGroupForm.isSubmitting && <FormLoader loading={true} />}
    </form>
  )
}

export default UserGroupForm